import * as React from 'react';
import { themr } from 'react-css-themr';
import isString from 'lodash/isString';
import classnames from 'classnames';
import theme from './ListCard.scss';
import DivLoader from 'common/components/core/wrappers/Div/DivLoader';
import TooltipDiv from 'common/components/core/wrappers/Div/TooltipDiv';
import MlFontIconLoader from 'common/components/core/loader/MlFontIconLoader';
import { UIEnum } from 'common/enums/UIEnums';

const defaults = {
    theme: {}
};

export const DefaultLeftIcon = ({
    isLoading,
    className,
    leftIconColor,
    leftIconBgColor,
    leftIcon,
    leftText,
    leftIconTooltip,
    theme
}) => {
    return isLoading || leftIcon || leftIconBgColor || leftText ? (
        leftIconTooltip ? (
            <TooltipDiv
                tooltipPosition="top"
                tooltipDelay={300}
                invert
                smallTooltip
                className={theme.leftIconWrap}
                tooltip={leftIconTooltip}
            >
                <MlFontIconLoader
                    loading={isLoading}
                    className={className}
                    color={leftIconColor}
                    bgColor={leftIconBgColor}
                    iconClass={leftIcon}
                    numberLabel={leftText}
                    theme={theme}
                />
            </TooltipDiv>
        ) : (
            <MlFontIconLoader
                loading={isLoading}
                className={className}
                color={leftIconColor}
                bgColor={leftIconBgColor}
                iconClass={leftIcon}
                numberLabel={leftText}
                theme={theme}
            />
        )
    ) : null;
};
export const DefaultRightIcon = ({
    isLoading,
    className,
    rightIconColor,
    rightIcon,
    onClick,
    testId,
    rightText,
    theme,
    disabled
}) => {
    return isLoading || rightIcon || rightIconColor || rightText ? (
        <MlFontIconLoader
            loading={isLoading}
            className={className}
            bgColor={rightIconColor}
            iconClass={rightIcon}
            onClick={onClick}
            numberLabel={rightText}
            theme={theme}
            testId={testId}
            disabled={disabled}
        />
    ) : null;
};
const DefaultTitle = ({ isLoading, theme, title }) => {
    return isLoading || title ? (
        <DivLoader
            loading={isLoading}
            className={theme.title}
            title={isString(title) ? title : ''}
        >
            {title}
        </DivLoader>
    ) : null;
};
const DefaultSubtitle = ({ isLoading, theme, subtitle }) => {
    return isLoading || subtitle ? (
        <DivLoader loading={isLoading} className={theme.subtitle}>
            {subtitle}
        </DivLoader>
    ) : null;
};
const DefaultExtraContent = ({ theme, extraContent }) => {
    return extraContent ? (
        <div className={theme.extraContentWrap}>{extraContent}</div>
    ) : null;
};
export const ListCardTypes = new UIEnum({
    Minimal: 'minimal'
});
const listCardFactory = (options = {}) => {
    const { theme: defaultTheme, themed = true } = { ...defaults, ...options };

    const ListCard = ({
        Title = DefaultTitle,
        Subtitle = DefaultSubtitle,
        LeftIcon = DefaultLeftIcon,
        RightIcon = DefaultRightIcon,
        ExtraContent = DefaultExtraContent
    }) => {
        class ListCardComponent extends React.PureComponent {
            handleClick = (...args) => {
                const { onClick, disabled, fieldId } = this.props;
                if (disabled) {
                    return;
                }
                if (fieldId) {
                    onClick && onClick(fieldId, ...args);
                } else {
                    onClick && onClick(...args);
                }
            };

            render() {
                const {
                    theme,
                    raised,
                    title,
                    className,
                    subtitle,
                    leftIcon,
                    leftIconColor,
                    leftIconTooltip,
                    leftText,
                    leftIconClass,
                    rightIcon,
                    rightText,
                    rightIconColor,
                    isSelected,
                    leftIconBgColor,
                    isLoading,
                    extraContent,
                    children,
                    style,
                    tabIndex,
                    onFocus,
                    onBlur,
                    onMouseEnter,
                    onMouseLeave,
                    onMouseMove,
                    onRightIconClick,
                    isCardDisabled,
                    isCardFocused,
                    cardType,
                    noPadding,
                    zIndexAuto,
                    autoWidth,
                    innerRef,
                    handleOverflow,
                    bgColor,
                    testId,
                    rightIconTestId,
                    ...others
                } = this.props;
                const cardTypeClass = ListCardTypes.getTypeClass({
                    type: cardType,
                    theme
                });
                const bgClassName = bgColor?.getClassName?.({
                    theme,
                    prefix: 'background'
                });
                return (
                    <div
                        testId={testId}
                        onFocus={onFocus}
                        tabIndex={tabIndex}
                        onBlur={onBlur}
                        style={style}
                        data-toolbox="card"
                        className={classnames(theme.card, {
                            [className]: className,
                            [theme.raised]: raised,
                            [theme.isSelected]: !!isSelected,
                            [theme.isCardDisabled]: !!isCardDisabled,
                            [theme.isCardFocused]: !!isCardFocused,
                            [cardTypeClass]: !!cardTypeClass,
                            [theme.noPadding]: noPadding,
                            [theme.zIndexAuto]: zIndexAuto,
                            [theme.autoWidth]: autoWidth,
                            [theme.handleOverflow]: handleOverflow,
                            [bgClassName]: !!bgClassName
                        })}
                        onClick={this.handleClick}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onMouseMove={onMouseMove}
                        ref={innerRef}
                    >
                        <div className={theme.cardTitle}>
                            {LeftIcon ? (
                                <LeftIcon
                                    isLoading={isLoading}
                                    className={classnames(
                                        theme.leftIcon,
                                        leftIconClass
                                    )}
                                    leftIconBgColor={leftIconBgColor}
                                    leftIconColor={leftIconColor}
                                    leftIconTooltip={leftIconTooltip}
                                    leftIcon={leftIcon}
                                    leftText={leftText}
                                    theme={theme}
                                    title={title}
                                    {...others}
                                />
                            ) : null}
                            <div className={theme.contentWrap}>
                                <div className={theme.left}>
                                    {Title ? (
                                        <Title
                                            theme={theme}
                                            title={title}
                                            isLoading={isLoading}
                                            {...others}
                                        />
                                    ) : null}
                                    {Subtitle ? (
                                        <Subtitle
                                            theme={theme}
                                            subtitle={subtitle}
                                            isLoading={isLoading}
                                            {...others}
                                        />
                                    ) : null}
                                </div>
                                <div className={theme.right}>
                                    {RightIcon ? (
                                        <RightIcon
                                            isLoading={isLoading}
                                            className={theme.rightIcon}
                                            rightIconColor={rightIconColor}
                                            rightIcon={rightIcon}
                                            rightText={rightText}
                                            theme={theme}
                                            onClick={onRightIconClick}
                                            testId={rightIconTestId}
                                            {...others}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        {ExtraContent ? (
                            <ExtraContent
                                {...this.props}
                                theme={theme}
                                extraContent={extraContent}
                            />
                        ) : null}
                        {children}
                    </div>
                );
            }
        }
        return themed
            ? themr('ListCard', defaultTheme)(ListCardComponent)
            : ListCardComponent;
    };
    return ListCard;
};

const unthemedFactory = listCardFactory({ themed: false });
const UnthemedListCard = unthemedFactory({});
const themedFactory = listCardFactory({ theme });
const ThemedListCard = themedFactory({});

export default themedFactory;
export {
    ThemedListCard as ListCard,
    themedFactory as listCardFactory,
    UnthemedListCard
};
